import { buildReverseIndex, projectEmailsFromIndex, pruneEmailIndexByIds, type EmailIndex } from '@/lib/emailMerging';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import { useEmailStore, pruneEmailsFromCache } from './emailStore';
import { useAccountStore } from './accountStore';
import { useFolderStore } from './folderStore';
import { useSenderGroupingStore } from './senderGroupingStore';

/**
 * Tracks recently removed email IDs to prevent realtime events from re-adding them.
 * IDs are automatically cleared after a timeout.
 *
 * Why this is 60s: IMAP/Gmail server-side reconciliation of a move operation can take
 * 10-30s. If the user navigates away from the inbox and back during that window, the
 * mailbox re-fetch may still return the archived email. The grace period must outlive
 * that round-trip or archived items will visibly reappear.
 */
const REMOVAL_GRACE_PERIOD_MS = 60_000;
// id -> the SOURCE folderId it was optimistically removed from (null when the
// source is unknown → suppress globally). Folder-scoped (R7/E8): the grace hides
// the stale copy in the SOURCE folder only, so the copy that has already moved
// to its DESTINATION folder shows immediately (archive-then-open-Archive,
// approve-into-inbox).
const recentlyRemovedByFolder = new Map<string, string | null>();
const removalTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Tracks senders currently being archived to prevent their already-existing
 * emails from being re-added during reload (even emails that weren't loaded
 * yet) while the server reconciles the move. We store the archive START time
 * per sender so a genuinely NEW message that arrives during the grace window —
 * one with a date newer than the archive action — is NOT suppressed (it was
 * never part of what the user archived). See `isSenderBeingArchived`.
 */
const sendersBeingArchived = new Map<string, number>();
// sender -> exempt ("kept"/pinned) email ids that must NOT be suppressed by the
// sender-wide archiving mark (E4: triage keeps some of a sender's mail).
const sendersBeingArchivedKept = new Map<string, Set<string>>();
const senderArchiveTimers = new Map<string, ReturnType<typeof setTimeout>>();

// Grace expiry must RE-PROJECT (N25): suppression is read-time, so when a mark
// times out (no echo ever arrived — e.g. SSE down) the suppressed-but-indexed
// rows only reappeared whenever some unrelated write happened to re-project,
// which read as emails randomly popping back in. Debounced: bulk removals
// expire many marks in the same tick.
let graceExpiryReprojectTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleGraceExpiryReproject() {
  if (graceExpiryReprojectTimer) {
    return;
  }
  graceExpiryReprojectTimer = setTimeout(() => {
    graceExpiryReprojectTimer = null;
    useEmailStore.getState().reprojectEmails();
  }, 50);
}

export function markSenderAsArchiving(senderEmail: string, keptIds?: string[]) {
  const normalizedEmail = senderEmail?.trim().toLowerCase();
  if (!normalizedEmail) return;


  // Clear existing timer if any
  const existingTimer = senderArchiveTimers.get(normalizedEmail);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }

  sendersBeingArchived.set(normalizedEmail, Date.now());
  // E4: record the "kept" (pinned) ids so isSenderBeingArchived lets them
  // through — otherwise a triage archive that keeps some of a sender's mail
  // suppresses the kept messages too and they vanish for the grace window.
  if (keptIds && keptIds.length) {
    sendersBeingArchivedKept.set(normalizedEmail, new Set(keptIds));
  } else {
    sendersBeingArchivedKept.delete(normalizedEmail);
  }

  // Set timer to clear after grace period
  const timer = setTimeout(() => {
    sendersBeingArchived.delete(normalizedEmail);
    sendersBeingArchivedKept.delete(normalizedEmail);
    senderArchiveTimers.delete(normalizedEmail);
    scheduleGraceExpiryReproject();
  }, REMOVAL_GRACE_PERIOD_MS);

  senderArchiveTimers.set(normalizedEmail, timer);
}

export function clearSenderArchiving(senderEmail: string) {
  const normalizedEmail = senderEmail?.trim().toLowerCase();
  if (!normalizedEmail) return;

  const timer = senderArchiveTimers.get(normalizedEmail);
  if (timer) {
    clearTimeout(timer);
    senderArchiveTimers.delete(normalizedEmail);
  }
  sendersBeingArchived.delete(normalizedEmail);
  sendersBeingArchivedKept.delete(normalizedEmail);
}

/**
 * Whether an email from this sender should be suppressed because the sender is
 * mid-archive. Pass the email's date so a message that arrived AFTER the archive
 * action started (genuinely new mail) is allowed through instead of being hidden
 * for the whole grace window. Omitting `emailDate` falls back to sender-only
 * suppression (used where a date isn't available).
 */
export function isSenderBeingArchived(
  senderEmail: string | null | undefined,
  emailDate?: string | number | Date | null,
  emailId?: string | null
): boolean {
  if (!senderEmail) return false;
  const normalizedEmail = senderEmail.trim().toLowerCase();
  const archivedAt = sendersBeingArchived.get(normalizedEmail);
  if (archivedAt === undefined) return false;

  // E4: a "kept" (pinned) message from this sender is exempt from suppression.
  if (emailId) {
    const kept = sendersBeingArchivedKept.get(normalizedEmail);
    if (kept && kept.has(emailId)) {
      return false;
    }
  }

  if (emailDate != null) {
    const emailMs = emailDate instanceof Date ? emailDate.getTime() : new Date(emailDate).getTime();
    // A message newer than the archive action was never part of what the user
    // archived — don't suppress it.
    if (Number.isFinite(emailMs) && emailMs > archivedAt) {
      return false;
    }
  }

  return true;
}

/**
 * Dirty-until-confirmed flag overlay (optimistic read/star).
 *
 * Optimistic read/star changes are applied to the store immediately, but the
 * authoritative server write can lag (an IMAP `set_flag`/`clear_flag` pending op
 * may take 10-30s to flush). Any index rebuild in that window — a folder switch,
 * sender-group exit, undo reload, or SSE reconnect — runs `loadEmails(reset)`,
 * which rebuilds `emailIndex` from the server payload and would re-assert the
 * stale server `read`/`starred`, making the optimistic change "revert on
 * refresh" (audit §2.10 #2/#4, symptom C).
 *
 * This overlay records the un-confirmed optimistic flags and is re-applied over
 * the server payload at every rebuild surface (`applyOverlayToEmail` /
 * `applyOverlayToIndex`), so server truth cannot clobber an un-flushed local
 * change. An entry self-clears the instant the server reflects the same value
 * (`confirmServerFlags`), with the TTL as a leak backstop. It also subsumes the
 * old realtime-echo suppression: while an entry exists `isRealtimeUpdateSuppressed`
 * is true, so a stale `email.updated` echo is ignored.
 */
type OptimisticFlags = { read?: boolean; starred?: boolean };
const OPTIMISTIC_FLAG_GRACE_MS = REMOVAL_GRACE_PERIOD_MS; // 60s — matches removal grace
const flagOverlay = new Map<string, OptimisticFlags>();
const flagOverlayTimers = new Map<string, ReturnType<typeof setTimeout>>();

function resetFlagOverlayTimer(emailId: string) {
  const existing = flagOverlayTimers.get(emailId);
  if (existing) {
    clearTimeout(existing);
  }
  const timer = setTimeout(() => {
    flagOverlay.delete(emailId);
    flagOverlayTimers.delete(emailId);
  }, OPTIMISTIC_FLAG_GRACE_MS);
  flagOverlayTimers.set(emailId, timer);
}

/** Record an optimistic read/star value (merges; does not drop the other flag). */
export function setOptimisticFlags(emailId: string, flags: OptimisticFlags) {
  if (!emailId) return;
  const current = flagOverlay.get(emailId) ?? {};
  const next: OptimisticFlags = { ...current };
  if (flags.read !== undefined) next.read = flags.read;
  if (flags.starred !== undefined) next.starred = flags.starred;
  flagOverlay.set(emailId, next);
  resetFlagOverlayTimer(emailId);
}

/** Hard-clear an overlay entry (rollback on failure, or removal of the email). */
export function clearOptimisticFlags(emailId: string) {
  const timer = flagOverlayTimers.get(emailId);
  if (timer) {
    clearTimeout(timer);
    flagOverlayTimers.delete(emailId);
  }
  flagOverlay.delete(emailId);
}

/**
 * Clear each tracked flag the server now agrees with; remove the entry entirely
 * once fully confirmed. Called from the realtime echo path and from loadEmails
 * after merging the server payload, so a reset that already matches self-cleans.
 */
export function confirmServerFlags(
  emailId: string,
  serverEmail: { read?: boolean; starred?: boolean } | null | undefined
) {
  if (!emailId || !serverEmail) return;
  const entry = flagOverlay.get(emailId);
  if (!entry) return;
  const next: OptimisticFlags = { ...entry };
  let changed = false;
  if (next.read !== undefined && serverEmail.read === next.read) {
    delete next.read;
    changed = true;
  }
  if (next.starred !== undefined && serverEmail.starred === next.starred) {
    delete next.starred;
    changed = true;
  }
  if (!changed) return;
  if (next.read === undefined && next.starred === undefined) {
    clearOptimisticFlags(emailId);
  } else {
    flagOverlay.set(emailId, next); // keep the existing timer
  }
}

/**
 * Generic echo suppression for a non-flag optimistic change (e.g. auto-mark on
 * open). Ensures an entry exists so isRealtimeUpdateSuppressed is true, without
 * disturbing any tracked flags.
 */
export function suppressRealtimeUpdate(emailId: string) {
  if (!emailId) return;
  if (!flagOverlay.has(emailId)) {
    flagOverlay.set(emailId, {});
  }
  resetFlagOverlayTimer(emailId);
}

export function isRealtimeUpdateSuppressed(emailId: string): boolean {
  return flagOverlay.has(emailId);
}

/**
 * Re-assert overlay flags over a single email/thread-message. Reference-stable:
 * returns the SAME object when there's no entry or no value differs, so it never
 * defeats projection's previous-ref short-circuit.
 */
export function applyOverlayToEmail<T extends { id?: string; read?: boolean; starred?: boolean }>(
  email: T
): T {
  const entry = email && email.id ? flagOverlay.get(email.id) : undefined;
  if (!entry) return email;
  let patched: T | null = null;
  if (entry.read !== undefined && email.read !== entry.read) {
    patched = { ...(patched ?? email), read: entry.read };
  }
  if (entry.starred !== undefined && email.starred !== entry.starred) {
    patched = { ...(patched ?? email), starred: entry.starred };
  }
  return patched ?? email;
}

/**
 * Re-assert overlay flags across an entire index. Reference-stable: returns the
 * same index object when nothing changed (clones lazily on first real diff).
 */
export function applyOverlayToIndex(index: EmailIndex): EmailIndex {
  if (flagOverlay.size === 0) return index;
  let next: EmailIndex | null = null;
  for (const key in index) {
    const email = index[key];
    const patched = applyOverlayToEmail(email);
    if (patched !== email) {
      if (!next) {
        next = { ...index };
      }
      next[key] = patched;
    }
  }
  return next ?? index;
}

export function markEmailsAsRemoved(
  emailIds: string[],
  folderById?: Map<string, string | null>
) {
  emailIds.forEach((id) => {
    // Clear existing timer if any
    const existingTimer = removalTimers.get(id);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    recentlyRemovedByFolder.set(id, folderById?.get(id) ?? null);

    // Set timer to clear after grace period
    const timer = setTimeout(() => {
      recentlyRemovedByFolder.delete(id);
      removalTimers.delete(id);
      scheduleGraceExpiryReproject();
    }, REMOVAL_GRACE_PERIOD_MS);

    removalTimers.set(id, timer);
  });
}

/**
 * Whether an email should be suppressed because it was just optimistically
 * removed. Pass the copy's own `folderId` so the grace applies only in the
 * SOURCE folder it was removed from — a copy already in its DESTINATION folder
 * (archive/trash/inbox after approve) must be visible immediately (R7/E8).
 * Omitting `folderId`, or a removal whose source was unknown, suppresses
 * globally (conservative).
 */
export function isRecentlyRemoved(emailId: string, folderId?: string | null): boolean {
  if (!recentlyRemovedByFolder.has(emailId)) {
    return false;
  }
  const fromFolder = recentlyRemovedByFolder.get(emailId) ?? null;
  if (fromFolder == null) {
    return true;
  }
  if (folderId == null) {
    return true;
  }
  return folderId === fromFolder;
}

/**
 * Read-time visibility predicate used by every list projection (see
 * MergeEmailOptions.visibilityFilter). Suppressed emails stay in the index but
 * are filtered out at projection time, so no write path can resurrect them and
 * "restore" is simply clearing the marks + re-projecting.
 */
export function visibleEmailFilter(email: { id: string; folderId?: string | null; from?: { email?: string | null } | null; date?: string | number | Date | null }): boolean {
  if (isRecentlyRemoved(email.id, email.folderId)) {
    return false;
  }
  if (isSenderBeingArchived(email.from?.email, email.date, email.id)) {
    return false;
  }
  return true;
}

export function clearRecentlyRemoved(emailId: string) {
  const timer = removalTimers.get(emailId);
  if (timer) {
    clearTimeout(timer);
    removalTimers.delete(emailId);
  }
  recentlyRemovedByFolder.delete(emailId);
}

export interface RemoveEmailsOptions {
  /**
   * Whether to stamp the 60s "recently removed" grace on the removed ids.
   * USER-INITIATED removals stamp (so realtime echoes of the user's own action
   * can't resurrect the row). PASSIVE event-driven evictions (a server rule
   * moved the message, a realtime update says it no longer belongs here) must
   * NOT stamp — the message legitimately lives elsewhere now, and the stamp
   * would hide it from its destination folder for up to a minute.
   */
  markRemoved?: boolean;
  /**
   * Who initiated the removal, deciding whether the removal may TOUCH THE READER.
   *
   * - `user` (the default): the user archived/deleted/moved the row, so
   *   auto-advancing to the next message (or closing when the folder empties)
   *   is the desired behavior.
   * - `passive`: a server rule / another device / a realtime `email.updated`
   *   evicted the row. The reader must be left EXACTLY as the user had it —
   *   auto-advancing or nulling the selection here is the reader-bounce (§1
   *   Link 3 / S2 / S3 / S5). Only the list index is pruned.
   *
   * Defaults from `markRemoved`: a passive eviction never stamps the grace, so
   * `markRemoved: false` ⇒ `passive` unless overridden.
   */
  initiator?: 'user' | 'passive';
}

/**
 * Removes the given email IDs from the email store and keeps selection/thread state in sync.
 */
export function removeEmailsFromStore(emailIds: string[], options: RemoveEmailsOptions = {}) {
  if (!emailIds.length) {
    return;
  }
  const markRemoved = options.markRemoved !== false;
  // A passive eviction never stamps the grace, so markRemoved:false ⇒ passive
  // unless the caller says otherwise. Passive removals prune the list only and
  // leave the reader untouched (§1 / S2 / S3 / S5).
  const initiator: 'user' | 'passive' =
    options.initiator ?? (markRemoved ? 'user' : 'passive');
  const syncSelection = initiator === 'user';

  if (markRemoved) {
    // Capture each id's SOURCE folder from the live index BEFORE it is pruned,
    // so the grace is folder-scoped (R7/E8): the stale copy hides in the source
    // folder only, while the moved copy shows immediately in its destination.
    const indexState = useEmailStore.getState();
    const folderById = new Map<string, string | null>();
    emailIds.forEach((id) => {
      const identity = indexState.emailReverseIndex.get(id);
      const email = identity ? indexState.emailIndex[identity] : null;
      folderById.set(id, email?.folderId ?? null);
    });
    // Mark as recently removed to prevent realtime events from re-adding
    markEmailsAsRemoved(emailIds, folderById);
    // A removed email's read/star overlay is moot and must not linger — otherwise a
    // later move-in of the same id could mis-apply a stale flag. Passive evictions
    // keep their overlay: the same message may be re-rendered in its destination
    // folder and an un-flushed optimistic flag should still apply there.
    emailIds.forEach(clearOptimisticFlags);
  }

  const idSet = new Set(emailIds);
  // Track the next email to select after removal (set inside setState, used after)
  const emailToSelectRef: { current: { id: string; accountId: string; folderId: string; threadId?: string | null } | null } = { current: null };

  useEmailStore.setState((state) => {
    let selectedEmailId = state.selectedEmailId;
    let selectedEmail = state.selectedEmail;
    let selectedThreadId = state.selectedThreadId;
    let selectedThread = state.selectedThread;
    // S3: any selection-null must invalidate any in-flight selectEmail so a
    // late commit can't reopen a torn reader (selectedThread set,
    // selectedEmailId null) after the close.
    let bumpSelectRequestId = false;

    // PASSIVE evictions (initiator: 'passive') NEVER touch the reader — the
    // message just moved/was removed elsewhere by a rule/another device, and
    // auto-advancing or nulling the open message here is the reader-bounce
    // (§1 Link 3 / S2). Only the list index below is pruned.
    if (syncSelection) {
      // E1 (thread-aware auto-advance): when the removed message(s) live inside
      // the OPEN thread and the thread still has surviving members, stay in the
      // thread on a surviving member. The projection-based advance below reads
      // the thread-COLLAPSED list, so archiving a non-representative thread
      // member missed in findIndex and nulled everything — the reader closed
      // though the thread still had messages. Only fall through to a list
      // neighbor when the open thread empties (or there is no open thread).
      const threadHasRemoved =
        !!selectedThread && selectedThread.messages.some((m) => idSet.has(m.id));
      const threadRemaining = selectedThread
        ? selectedThread.messages.filter((m) => !idSet.has(m.id))
        : [];

      if (selectedThread && threadHasRemoved && threadRemaining.length > 0) {
        selectedThread = {
          ...selectedThread,
          messages: threadRemaining,
          messageCount: threadRemaining.length,
          unreadCount: threadRemaining.filter((m) => !m.read).length,
        };
        // selectedThreadId is unchanged. Move the highlight only when the
        // highlighted message was the one removed; the surviving thread
        // messages already carry their bodies, so no re-fetch is needed.
        if (!selectedEmailId || idSet.has(selectedEmailId)) {
          const fallback = threadRemaining[threadRemaining.length - 1];
          selectedEmailId = fallback.id;
          selectedEmail = fallback;
        } else if (selectedEmail && idSet.has(selectedEmail.id)) {
          selectedEmail =
            threadRemaining.find((m) => m.id === selectedEmailId) ?? selectedEmail;
        }
      } else {
        // No surviving open thread — advance within the list projection.
        if (selectedEmailId && idSet.has(selectedEmailId)) {
          const currentIndex = state.emails.findIndex((e) => e.id === selectedEmailId);
          let nextEmail = null;

          // Helper to check if an email is a valid next selection
          // Excludes: emails being removed, and pending senders
          const isValidNextEmail = (email: typeof state.emails[0]) =>
            !idSet.has(email.id) && email.contactApprovalState !== 'pending';

          if (currentIndex !== -1) {
            // Look for next email that isn't being removed and isn't from pending sender
            for (let i = currentIndex + 1; i < state.emails.length; i++) {
              if (isValidNextEmail(state.emails[i])) {
                nextEmail = state.emails[i];
                break;
              }
            }
            // If no next email, look backwards
            if (!nextEmail) {
              for (let i = currentIndex - 1; i >= 0; i--) {
                if (isValidNextEmail(state.emails[i])) {
                  nextEmail = state.emails[i];
                  break;
                }
              }
            }
          }

          if (nextEmail) {
            // Schedule proper email selection after state update
            emailToSelectRef.current = {
              id: nextEmail.id,
              accountId: nextEmail.accountId,
              folderId: nextEmail.folderId ?? '',
              threadId: nextEmail.threadId,
            };
            // Clear selection - will be re-set by selectEmail
            selectedEmailId = nextEmail.id;
            selectedEmail = null;
            selectedThreadId = null;
            selectedThread = null;
          } else {
            selectedEmailId = null;
            selectedEmail = null;
            selectedThreadId = null;
            selectedThread = null;
            bumpSelectRequestId = true;
          }
        }

        if (selectedEmail && idSet.has(selectedEmail.id)) {
          selectedEmail = null;
          selectedEmailId = null;
          bumpSelectRequestId = true;
        }

        if (selectedThread && !emailToSelectRef.current) {
          const filteredMessages = selectedThread.messages.filter((message) => !idSet.has(message.id));
          if (filteredMessages.length === 0) {
            selectedThread = null;
            selectedThreadId = null;
            bumpSelectRequestId = true;
          } else if (filteredMessages.length !== selectedThread.messages.length) {
            selectedThread = {
              ...selectedThread,
              messages: filteredMessages,
              messageCount: filteredMessages.length,
              unreadCount: filteredMessages.filter((message) => !message.read).length,
            };
            if (
              selectedEmail &&
              !filteredMessages.some((message) => message.id === selectedEmail!.id)
            ) {
              const fallback = filteredMessages[filteredMessages.length - 1] ?? null;
              selectedEmail = fallback;
              selectedEmailId = fallback?.id ?? null;
            }
          }
        }
      }
    }

    // Offset bookkeeping (A4): removed rows shrink the server-side result set,
    // so the next "load more" must not skip that many older rows. Decrement the
    // removed email's own (account, folder) offset and — in the unified view —
    // the active unified mailbox key, which aggregates every source.
    const selectedAccountId = useAccountStore.getState().selectedAccountId;
    const selectedFolderId = useFolderStore.getState().selectedFolderId();
    const unifiedKey =
      selectedAccountId === UNIFIED_ACCOUNT_ID && selectedFolderId
        ? `${selectedAccountId}:${selectedFolderId}`
        : null;
    const offsetDecrements = new Map<string, number>();
    Object.values(state.emailIndex).forEach((email) => {
      if (!email || !idSet.has(email.id)) {
        return;
      }
      const ownKey = `${email.accountId}:${email.folderId ?? ''}`;
      if (state.mailboxOffsets[ownKey] !== undefined) {
        offsetDecrements.set(ownKey, (offsetDecrements.get(ownKey) ?? 0) + 1);
      }
      if (unifiedKey && state.mailboxOffsets[unifiedKey] !== undefined) {
        offsetDecrements.set(unifiedKey, (offsetDecrements.get(unifiedKey) ?? 0) + 1);
      }
    });
    let nextOffsets = state.mailboxOffsets;
    if (offsetDecrements.size > 0) {
      nextOffsets = { ...state.mailboxOffsets };
      offsetDecrements.forEach((count, key) => {
        nextOffsets[key] = Math.max(0, (nextOffsets[key] ?? 0) - count);
      });
    }

    const nextIndex = pruneEmailIndexByIds(state.emailIndex, idSet);
    const nextReverse = nextIndex !== state.emailIndex ? buildReverseIndex(nextIndex) : state.emailReverseIndex;
    // Pass `previous` so the projection's reference-stabilization short-circuit
    // can reuse unchanged element refs — removing one row shouldn't churn every
    // other row's identity and trigger needless re-renders / scroll jumps.
    const nextEmails = projectEmailsFromIndex(
      nextIndex,
      { visibilityFilter: visibleEmailFilter },
      state.emails
    );
    const nextCache = pruneEmailsFromCache(state.emailCache, idSet);

    return {
      emails: nextEmails,
      emailIndex: nextIndex,
      emailReverseIndex: nextReverse,
      mailboxOffsets: nextOffsets,
      selectedEmailId,
      selectedEmail,
      selectedThread,
      selectedThreadId,
      emailCache: nextCache,
      emailDetailLoading: emailToSelectRef.current !== null,
      ...(bumpSelectRequestId
        ? { emailSelectRequestId: state.emailSelectRequestId + 1 }
        : {}),
    };
  });

  // After state update, trigger proper email selection to load full content
  if (emailToSelectRef.current) {
    const target = emailToSelectRef.current;
    void useEmailStore
      .getState()
      .selectEmail(target.id, target.accountId, target.folderId, {
        threadId: target.threadId ?? null,
        // silentFailure + graceful fallback (S3 / §1 Link 3): the auto-advance
        // neighbor can 404 (already moved server-side) or hit a network blip.
        // Don't toast + bounce to the list scarily; if it failed and nothing
        // newer superseded this advance, quietly drop to the list.
        silentFailure: true,
      })
      .then((ok) => {
        if (ok) return;
        const st = useEmailStore.getState();
        if (st.selectedEmailId === target.id && !st.selectedEmail) {
          st.clearSelection();
        }
      });
  }

  // Empty-group exit only for USER-initiated removals (S5): a passive eviction
  // that empties the projection (a rule/another device cleared the group the
  // user is reading) must NOT yank them out of the group.
  if (initiator === 'user') {
    const senderGroupingState = useSenderGroupingStore.getState();
    if (senderGroupingState.activeSenderGroup) {
      const remainingEmails = useEmailStore.getState().emails;
      if (remainingEmails.length === 0) {
        void senderGroupingState.exitSenderGroup();
      }
    }
  }
}
